/**
 * @bos/portal3 — the DATA MODEL review (batch 5) and the revision loop (batch 6): the
 * screen a person reads when the run stops after deriving the data model, the door that
 * approves it, the door that DISAGREES with it in their own words, and the diff that
 * shows exactly what a re-derivation changed against the version they read. This is the
 * heart of the product — everything before it was plumbing so this could exist.
 *
 * HONESTY RULES:
 *   - Everything rendered is a projection of bytes the run wrote (`runs.dataModel` +
 *     `runs.dataModelDiff`, shaped server-side). No cost, no timings, no percentages,
 *     no provenance the artifacts don't carry.
 *   - The dossier notes are the machine's OWN reasons, written at the moment of each
 *     decision — rendered verbatim, attached to the entity/field/relationship they name.
 *     Where nothing was reasoned, nothing is said. The diff attaches the same words to
 *     the changes they explain.
 *   - THE PICTURE (phase 2) draws the SAME payload the cards render — a purpose-built SVG
 *     canvas (`erLayout.ts` + `DataModelCanvas.tsx`), never `er-diagram.mmd`, never a second
 *     fetch. The two views cannot disagree because there is one set of bytes under both, and
 *     the Cards stay the equal-rights path: they carry the dossier notes the Picture omits.
 *     Gestures on the canvas are a compiler front-end for the SAME typed edits — one code
 *     path, one dry run, one basket. The canvas offers exactly the six kinds that exist.
 *   - The approval is BOUND BY SIGNATURE to the exact model it approved (newpip F6). The
 *     screen says so after approving — if the model is re-derived, the approval stops
 *     binding and the run pauses here again. That is correct behaviour, made legible.
 *   - DISAGREEING states its consequence BEFORE it runs, in WORK and not in numbers (no
 *     price, no duration, no percentage exists): the data model is derived again, and
 *     everything after it too, because it all builds on what changes here.
 *   - The diff never implies the request was honoured: a re-derivation that came back
 *     IDENTICAL says so in so many words ("you asked — and the model is unchanged"),
 *     and a changed model shows EVERYTHING that moved, including what nobody asked for.
 *     Hiding either would be the worst thing this screen could do.
 *   - THE BASKET (the basket batch): several exact edits compose into one ordered list,
 *     previewed TOGETHER by the server chaining the dry run (edit #2 evaluated against
 *     the model as #1 left it — never against bytes that will no longer exist), applied
 *     as ONE commitment with ONE resume, all-or-nothing on any refusal — and every
 *     refusal attributed to the exact edit that caused it, so a person fixes or drops
 *     that one without discarding the set.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  ApplyDataModelBasketResult,
  ApproveDataModelResult,
  DataModelDiff,
  DataModelEdit,
  EditDataModelResult,
  OwnerLedgerHistoryRow,
  RequestDataModelRevisionResult,
  RevisionComment,
  RunDataModelBasketPreview,
  RunDataModelDiffResult,
  RunDataModelEditPreview,
  RunDataModelResult,
  RunIntakeSurface,
} from '@bos/portal3-server';
import type { DataModelEntity, DataModelNote } from '@bos/portal3-server';
import { DataModelCanvas, type CanvasGestures } from './DataModelCanvas';
import { OrdersTray } from './OrdersTray';
import { runErrorMessage } from './hooks';

/** One dossier note: `about` (the task's word / the concept) then the machine's reason. */
function Note({ note }: { note: DataModelNote }) {
  return (
    <li className="dm-note">
      {note.about !== undefined && <span className="dm-note__about">{note.about}</span>}
      <span className="dm-note__reason">{note.reason}</span>
    </li>
  );
}

function NoteList({ notes }: { notes: readonly DataModelNote[] }) {
  if (notes.length === 0) return null; // nothing was reasoned — say nothing
  return <ul className="dm-notes">{notes.map((n, i) => <Note key={i} note={n} />)}</ul>;
}

interface EntityCardProps {
  readonly entity: DataModelEntity;
  /** The disagree affordance shows only while the door is open (there is a model on offer). */
  readonly revisable: boolean;
  /** The open composer's text for this entity; undefined = composer closed. */
  readonly draft: string | undefined;
  readonly onOpen: () => void;
  readonly onDraft: (text: string) => void;
  /** The EDIT door (the exact half): open the change composer for this entity. */
  readonly onOpenEdit: () => void;
  /** The open edit composer, rendered inside the card; null = closed. */
  readonly editor: React.ReactNode;
}

function EntityCard({ entity, revisable, draft, onOpen, onDraft, onOpenEdit, editor }: EntityCardProps) {
  return (
    <article className="dm-entity" aria-label={entity.name}>
      <h4 className="dm-entity__name">
        {entity.name}
        {revisable && editor === null && (
          <button className="btn btn--quiet btn--sm dm-entity__edit" type="button" onClick={onOpenEdit}>
            Change something
          </button>
        )}
        {revisable && draft === undefined && (
          <button className="btn btn--quiet btn--sm dm-entity__disagree" type="button" onClick={onOpen}>
            Disagree
          </button>
        )}
      </h4>
      {entity.summary !== null && <p className="dm-entity__summary">{entity.summary}</p>}
      <table className="dm-fields">
        <tbody>
          {entity.fields.map((f) => (
            <tr key={f.name}>
              <th scope="row" className="dm-fields__name">
                {f.name}
                {f.name === entity.identityField && <span className="dm-chip dm-chip--id">identity</span>}
              </th>
              <td className="dm-fields__type">
                {f.type}
                {f.format !== undefined && <span className="dm-fields__ann"> · {f.format}</span>}
                {f.enumRef !== undefined && <span className="dm-fields__ann"> · values from {f.enumRef}</span>}
                {f.notes.length > 0 && <NoteList notes={f.notes} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <NoteList notes={entity.notes} />
      {editor}
      {revisable && draft !== undefined && (
        <div className="dm-revise__box">
          <label className="dm-revise__label" htmlFor={`dm-revise-${entity.name}`}>
            What’s wrong with {entity.name}? Your own words — name the field if it’s one field.
          </label>
          <textarea
            id={`dm-revise-${entity.name}`}
            className="dm-revise__text"
            rows={2}
            value={draft}
            onChange={(e) => onDraft(e.target.value)}
          />
        </div>
      )}
    </article>
  );
}

// ── the EDIT composer (the exact door — the disagree box's precise sibling) ──

const EDIT_FIELD_TYPES = ['string', 'string?', 'number', 'number?', 'boolean', 'boolean?'] as const;
type EditFieldType = (typeof EDIT_FIELD_TYPES)[number];

type EditOp = DataModelEdit['op'];

/** The operations, in the owner's words. Entity ops name the entity when rendered. */
const EDIT_OPS: ReadonlyArray<readonly [EditOp, string]> = [
  ['rename-field', 'Rename a field'],
  ['retype-field', 'Change a field’s type'],
  ['add-field', 'Add a field'],
  ['remove-field', 'Remove a field'],
  ['rename-entity', 'Rename it'],
  ['remove-entity', 'Remove it from the model'],
];

/**
 * A gesture's compiled intent, handed to the composer: the operation it picked, the field it
 * named, and whether the dry run can run at once. THE SEED CHANGES NOTHING ABOUT THE DOOR —
 * it fills in the same form a person would fill in by hand, and the same preview and the same
 * two buttons decide everything after that. `token` is bumped per gesture so that clicking the
 * same field twice re-seeds instead of silently doing nothing.
 */
export interface EditComposerSeed {
  readonly op: EditOp;
  readonly field?: string;
  /** Only ever set for a COMPLETE gesture (the two removals name everything they need). */
  readonly autoPreview?: boolean;
  readonly token: number;
}

/** The edit a seed already fully determines, or null while it still needs the person's input. */
function seededEdit(entity: DataModelEntity, seed: EditComposerSeed): DataModelEdit | null {
  if (seed.op === 'remove-field' && seed.field !== undefined) {
    return { op: 'remove-field', entity: entity.name, field: seed.field };
  }
  if (seed.op === 'remove-entity') return { op: 'remove-entity', entity: entity.name };
  return null;
}

interface EditComposerProps {
  readonly entity: DataModelEntity;
  /** A gesture from the Picture, prefilling this composer; null when it was opened by hand. */
  readonly seed: EditComposerSeed | null;
  /** The seed was applied to the form — the parent clears it, so it can never replay on a
   *  later remount of this composer (a gesture is one instruction, not a standing one). */
  readonly onSeedConsumed?: () => void;
  readonly onPreview: (edit: DataModelEdit) => Promise<RunDataModelEditPreview>;
  readonly onApply: (edit: DataModelEdit) => Promise<EditDataModelResult>;
  readonly onClose: () => void;
  /** Add the composed edit to the BASKET instead of applying it now. */
  readonly onAdd: (edit: DataModelEdit) => void;
  /** How many edits the basket already holds. Non-zero changes the composer's meaning: the
   *  preview is CHAINED after the basket (the parent wires `onPreview` accordingly) and the
   *  immediate-apply act disappears — committing one edit while others are pending would be
   *  exactly the blind sequential commit the basket exists to end. */
  readonly basketCount: number;
  /** Bumped on every basket mutation: a chained preview describing a basket that no longer
   *  exists must never stay on screen. */
  readonly basketVersion: number;
}

/**
 * One entity's edit composer: pick an operation, see EXACTLY what it will do (the server's dry run over
 * the real bytes — changes, cautions, refusals), then apply — or add it to the basket to land several
 * edits as one. Nothing is committed before the preview is shown and the Apply button pressed (or, in
 * basket mode, before the BASKET is applied); changing any input clears a stale preview (a preview must
 * only ever describe the edit on screen).
 */
function EditComposer({ entity, seed, onSeedConsumed, onPreview, onApply, onClose, onAdd, basketCount, basketVersion }: EditComposerProps) {
  const firstField = entity.fields[0]?.name ?? '';
  const [op, setOp] = useState<EditOp>(seed?.op ?? 'rename-field');
  const [field, setField] = useState(seed?.field ?? firstField);
  const [name, setName] = useState('');
  const [type, setType] = useState<EditFieldType>('string');
  const [format, setFormat] = useState<'' | 'date' | 'datetime'>('');
  const [preview, setPreview] = useState<RunDataModelEditPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  /** The control the gesture pointed at — focused one render later, once it exists. */
  const [focusId, setFocusId] = useState<string | null>(null);

  const needsField = op === 'rename-field' || op === 'retype-field' || op === 'remove-field';
  const needsName = op === 'rename-field' || op === 'add-field' || op === 'rename-entity';
  const needsType = op === 'retype-field' || op === 'add-field';
  const textual = type === 'string' || type === 'string?';

  /** The edit as composed, or null while an input it needs is still empty. */
  function buildEdit(): DataModelEdit | null {
    switch (op) {
      case 'rename-field':
        return field !== '' && name.trim() !== '' ? { op, entity: entity.name, from: field, to: name.trim() } : null;
      case 'retype-field':
        return field !== '' ? { op, entity: entity.name, field, type } : null;
      case 'add-field':
        return name.trim() !== ''
          ? { op, entity: entity.name, field: name.trim(), type, ...(format !== '' && textual ? { format } : {}) }
          : null;
      case 'remove-field':
        return field !== '' ? { op, entity: entity.name, field } : null;
      case 'rename-entity':
        return name.trim() !== '' ? { op, from: entity.name, to: name.trim() } : null;
      case 'remove-entity':
        return { op, entity: entity.name };
    }
  }
  const edit = buildEdit();

  /** THE STALE-RESPONSE GUARD. Two rapid gestures race two dry runs; without this, the
   *  last-RESOLVING response wins the pane while Apply commits the edit built from the
   *  CURRENT form — the preview would describe edit X over a button that commits edit Y.
   *  `previewSeq` numbers every request; `previewWant` names the one whose response may
   *  still land (0 = none). Every path that makes a shown preview stale also invalidates
   *  any in-flight one, so nothing superseded can ever land late. */
  const previewSeq = useRef(0);
  const previewWant = useRef(0);
  function invalidatePreview() {
    previewWant.current = 0;
  }

  /** Any input change makes a shown preview stale — drop it rather than let it describe the wrong edit. */
  function set<T>(setter: (v: T) => void): (v: T) => void {
    return (v) => {
      setter(v);
      invalidatePreview();
      setPreview(null);
      setNote(null);
    };
  }

  async function runPreviewOf(candidate: DataModelEdit) {
    const req = ++previewSeq.current;
    previewWant.current = req;
    setBusy(true);
    setNote(null);
    try {
      const result = await onPreview(candidate);
      if (previewWant.current !== req) return; // superseded — it must never land under the wrong edit
      setPreview(result);
    } catch (err) {
      if (previewWant.current !== req) return; // a superseded failure is not this edit's failure
      setNote(runErrorMessage(err));
    } finally {
      // Only the LATEST-STARTED request may settle the busy flag; an older one yielding
      // here would re-enable the buttons while a newer dry run is still in flight.
      if (previewSeq.current === req) setBusy(false);
    }
  }

  async function runPreview() {
    if (edit === null) return;
    await runPreviewOf(edit);
  }

  async function apply() {
    if (edit === null) return;
    invalidatePreview(); // an in-flight dry run must not repaint the pane mid-apply
    setBusy(true);
    setNote(null);
    try {
      const result = await onApply(edit);
      setNote(editResultSentence(result));
      setPreview(null);
    } catch (err) {
      setNote(runErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  /** Into the basket: nothing is sent anywhere — the basket panel is where the set commits. */
  function add() {
    if (edit === null) return;
    invalidatePreview();
    onAdd(edit);
    setPreview(null);
    setNote('Added to the basket — nothing is committed until you apply the basket.');
  }

  // A basket mutation makes a CHAINED preview stale (it described the edit landing after a
  // basket that no longer exists in that form) — drop it, shown AND in flight, rather than
  // let it mislead.
  useEffect(() => {
    invalidatePreview();
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketVersion]);

  const ids = `dm-edit-${entity.name}`;

  /* A GESTURE FROM THE PICTURE fills this form in and nothing else. A COMPLETE gesture (the
     two removals name everything an edit needs) also runs the dry run at once, so the
     server's own sentence — "This will change the model exactly like this" and every caution
     — is on screen before any button that commits exists. An INCOMPLETE gesture puts the
     cursor in the input it still needs and waits for the person, exactly like the hand path. */
  const seedToken = seed?.token;
  useEffect(() => {
    if (seed === null || seed === undefined || seedToken === undefined) return;
    setOp(seed.op);
    if (seed.field !== undefined) setField(seed.field);
    setName('');
    setNote(null);
    invalidatePreview(); // a dry run an earlier gesture started must not land under this one
    setPreview(null);
    const complete = seededEdit(entity, seed);
    // A seed is CONSUMED the moment it has filled the form: the parent nulls it, so a later
    // unrelated remount of this composer (a view toggle, the next halt) replays nothing —
    // least of all a destructive dry run nobody just asked for.
    onSeedConsumed?.();
    if (seed.autoPreview === true && complete !== null) {
      void runPreviewOf(complete);
      return;
    }
    setFocusId(seed.op === 'retype-field' ? `${ids}-type` : `${ids}-name`);
    // Re-seeding is keyed on the gesture's own token: the same gesture twice must re-fill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedToken]);

  // Focus lands one render later — the control the gesture points at may not exist until the
  // seeded operation has rendered its own inputs.
  useEffect(() => {
    if (focusId === null) return;
    document.getElementById(focusId)?.focus();
    setFocusId(null);
  }, [focusId]);

  return (
    <div className="dm-edit" role="region" aria-label={`Change ${entity.name}`}>
      <div className="dm-edit__row">
        <label className="dm-edit__label" htmlFor={`${ids}-op`}>
          What do you want to change?
        </label>
        <select id={`${ids}-op`} className="dm-edit__select" value={op} onChange={(e) => set(setOp)(e.target.value as EditOp)}>
          {EDIT_OPS.map(([value, label]) => (
            <option key={value} value={value}>
              {value === 'rename-entity' ? `Rename ${entity.name}` : value === 'remove-entity' ? `Remove ${entity.name} from the model` : label}
            </option>
          ))}
        </select>
      </div>
      {needsField && (
        <div className="dm-edit__row">
          <label className="dm-edit__label" htmlFor={`${ids}-field`}>
            Which field?
          </label>
          <select id={`${ids}-field`} className="dm-edit__select" value={field} onChange={(e) => set(setField)(e.target.value)}>
            {entity.fields.map((f) => (
              <option key={f.name} value={f.name}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {needsName && (
        <div className="dm-edit__row">
          <label className="dm-edit__label" htmlFor={`${ids}-name`}>
            {op === 'add-field' ? 'Field name' : 'New name'}
          </label>
          <input
            id={`${ids}-name`}
            className="dm-edit__input"
            type="text"
            value={name}
            onChange={(e) => set(setName)(e.target.value)}
          />
        </div>
      )}
      {needsType && (
        <div className="dm-edit__row">
          <label className="dm-edit__label" htmlFor={`${ids}-type`}>
            Type
          </label>
          <select id={`${ids}-type`} className="dm-edit__select" value={type} onChange={(e) => set(setType)(e.target.value as EditFieldType)}>
            {EDIT_FIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      )}
      {op === 'add-field' && textual && (
        <div className="dm-edit__row">
          <label className="dm-edit__label" htmlFor={`${ids}-format`}>
            Holds a date?
          </label>
          <select
            id={`${ids}-format`}
            className="dm-edit__select"
            value={format}
            onChange={(e) => set(setFormat)(e.target.value as '' | 'date' | 'datetime')}
          >
            <option value="">no — plain text</option>
            <option value="date">a date</option>
            <option value="datetime">a date and time</option>
          </select>
        </div>
      )}

      {preview === null && (
        <div className="dm-edit__actions">
          <button className="btn btn--ghost btn--sm" type="button" onClick={() => void runPreview()} disabled={busy || edit === null}>
            {busy ? 'Checking…' : basketCount > 0 ? 'See what this will do after the basket' : 'See what this will do'}
          </button>
          <button className="btn btn--ghost btn--sm" type="button" onClick={add} disabled={busy || edit === null}>
            Add to basket
          </button>
          <button className="btn btn--quiet btn--sm" type="button" onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>
      )}

      {preview !== null && preview.kind === 'refused' && (
        <div className="dm-edit__preview dm-edit__preview--refused" role="region" aria-label="Cannot be applied">
          <p className="dm-edit__sentence">This edit cannot be applied as things stand:</p>
          <ul className="dm-edit__list">
            {preview.refusals.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
          <p className="dm-edit__sentence">Nothing has been changed.</p>
          <div className="dm-edit__actions">
            <button className="btn btn--quiet btn--sm" type="button" onClick={() => setPreview(null)} disabled={busy}>
              Back
            </button>
          </div>
        </div>
      )}

      {preview !== null && preview.kind === 'no-change' && (
        <div className="dm-edit__preview dm-edit__preview--nochange" role="region" aria-label="Nothing would change">
          <p className="dm-edit__sentence">{preview.reason} — there is nothing to apply.</p>
          <div className="dm-edit__actions">
            <button className="btn btn--quiet btn--sm" type="button" onClick={() => setPreview(null)} disabled={busy}>
              Back
            </button>
          </div>
        </div>
      )}

      {preview !== null && preview.kind === 'change' && (
        <div className="dm-edit__preview" role="region" aria-label="Before it runs">
          <p className="dm-edit__sentence">This will change the model exactly like this:</p>
          <ul className="dm-edit__list">
            {preview.changes.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
          {preview.cautions.length > 0 && (
            <>
              <p className="dm-edit__sentence">Worth a second look:</p>
              <ul className="dm-edit__list dm-edit__list--cautions">
                {preview.cautions.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </>
          )}
          {basketCount > 0 ? (
            <p className="dm-edit__consequence">
              Previewed as it lands AFTER the {basketCount} edit{basketCount === 1 ? '' : 's'} already in your
              basket — on the model as they leave it. Nothing is committed until you apply the basket.
            </p>
          ) : (
            <p className="dm-edit__consequence">
              This edit is exact — it is applied as ordered, not asked for. Making it needs no new derivation
              and spends nothing.{' '}
              {preview.laterStagesDerived
                ? 'Parts of the plan already built on the data model will be rebuilt from the edited model when the run continues — that is paid work.'
                : 'Nothing has been built on the data model yet, so nothing is redone — everything later simply builds on the edited model.'}{' '}
              The run pauses here again so you can see the change before approving.
            </p>
          )}
          {preview.alreadyOrdered && (
            <p className="dm-edit__sentence">This exact edit is already recorded — it cannot be ordered twice.</p>
          )}
          <div className="dm-edit__actions">
            {basketCount === 0 && (
              <button className="btn btn--primary btn--sm" type="button" onClick={() => void apply()} disabled={busy || preview.alreadyOrdered}>
                {busy ? 'Applying…' : 'Apply this edit'}
              </button>
            )}
            <button
              className={`btn ${basketCount > 0 ? 'btn--primary' : 'btn--ghost'} btn--sm`}
              type="button"
              onClick={add}
              disabled={busy || preview.alreadyOrdered}
            >
              Add to basket
            </button>
            <button className="btn btn--quiet btn--sm" type="button" onClick={() => setPreview(null)} disabled={busy}>
              Keep reading
            </button>
          </div>
        </div>
      )}

      {note !== null && (
        <p className="dm-door__note" role="status">
          {note}
        </p>
      )}
    </div>
  );
}

/** The sentence the approve outcome earns — each one a fact the server reported. */
export function approveResultSentence(result: ApproveDataModelResult): string {
  if (result.resumed) return 'Approved — the machine is continuing.';
  if (result.openDecisions > 0) {
    return `Approved and recorded — but ${result.openDecisions} decision${result.openDecisions === 1 ? '' : 's'} about this model still need${result.openDecisions === 1 ? 's' : ''} your answer before the machine continues.`;
  }
  if (result.alreadyApproved) return 'This exact model was already approved — nothing changed.';
  return 'Approved and recorded.';
}

/** The sentence a revision submission earns — each one a fact the server reported. */
export function revisionResultSentence(result: RequestDataModelRevisionResult): string {
  if (result.reason === 'already-recorded') {
    return 'Those words are already recorded — the machine has them. Nothing new was sent.';
  }
  if (result.resumed) return 'Your words are recorded — the machine is deriving the data model again.';
  return 'Your words are recorded — the machine will derive the data model again the next time it runs.';
}

/** The sentence an edit submission earns — each one a fact the server reported. */
export function editResultSentence(result: EditDataModelResult): string {
  switch (result.outcome) {
    case 'applied':
      return result.resumed
        ? 'Your edit is being applied — the model will pause here again showing the change.'
        : 'Your edit is recorded — it is applied the next time the run continues.';
    case 'no-change':
      return `${result.reason}. Nothing was changed.`;
    case 'already-ordered':
      return result.resumed
        ? 'That exact edit was already ordered — it is not ordered twice; it is being applied now.'
        : 'That exact edit was already ordered — it is not ordered twice.';
    case 'refused':
      return `The edit was not applied: ${result.refusals.join('; ')}.`;
  }
}

/** The sentence a basket apply earns — each one a fact the server reported. */
export function basketResultSentence(result: ApplyDataModelBasketResult): string {
  switch (result.outcome) {
    case 'applied': {
      const noop =
        result.noopCount > 0
          ? ` ${result.noopCount} of your edits ordered nothing — the model already said it.`
          : '';
      return result.resumed
        ? `Your ${result.appliedCount} edit${result.appliedCount === 1 ? ' is' : 's are'} being applied as one — the model will pause here again showing the combined change.${noop}`
        : `Your ${result.appliedCount} edit${result.appliedCount === 1 ? ' is' : 's are'} recorded as one — they are applied the next time the run continues.${noop}`;
    }
    case 'nothing-to-apply':
      return 'Everything in the basket was already true — nothing was changed.';
    case 'refused':
      return 'Nothing was applied: the basket contains an edit that refuses. A basket applies all-or-nothing — fix or remove the edit named above; the rest of the basket stays.';
  }
}

/** The door's opening sentence, per the reason the chain published for this pause. */
function doorSentence(approval: RunDataModelResult['approval']): string {
  if (approval.state === 'stale') {
    return 'Your recorded approval no longer matches the model on offer — read it again and re-approve.';
  }
  if (approval.state !== 'awaiting') return '';
  switch (approval.reason) {
    case 'superseded':
      // The chain's own owner-register sentence for this case (er-approval.ts) — the
      // one fact that matters: the thing you approved is not the thing in front of you.
      return 'Your earlier approval no longer applies — the data model has been rebuilt since you approved it. Have a look at the new one and approve it again.';
    case 'unsigned':
      return 'Your earlier approval could not be matched to this data model, so it no longer applies. Have a look at what we understood and approve it again.';
    case 'decisions-open':
      return 'Your approval is on hold — decisions about this data model still need your answer, and answering one can change what you approved.';
    default:
      return 'This is the data model the machine derived from your task. Read it, and approve it to continue — nothing more is spent while it waits.';
  }
}

// ── the diff panel (batch 6) ─────────────────────────────────────────────────

/** The person's request, quoted back beside what it came to. */
function CommentsQuote({ comments }: { comments: readonly RevisionComment[] }) {
  if (comments.length === 0) return null;
  return (
    <div className="dm-diff__asked">
      <span className="dm-diff__asked-head">You asked:</span>
      <ul className="dm-diff__asked-list">
        {comments.map((c, i) => (
          <li key={i}>
            {c.about !== undefined && <span className="dm-diff__asked-about">{c.about}: </span>}
            <span className="dm-diff__asked-text">“{c.text}”</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DiffGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="dm-diff__group">
      <h4 className="dm-diff__group-title">{title}</h4>
      {children}
    </div>
  );
}

function ChangedModel({ diff }: { diff: DataModelDiff }) {
  if (diff.changedCount === 0) {
    return (
      <p className="dm-diff__empty">
        The model was rebuilt, but nothing this screen shows moved — the differences are in details this
        view does not display.
      </p>
    );
  }
  return (
    <div className="dm-diff__changes">
      {diff.entitiesAdded.length > 0 && (
        <DiffGroup title="Now tracked (new)">
          <ul className="dm-diff__list">
            {diff.entitiesAdded.map((e) => (
              <li key={e.name} className="dm-diff__row dm-diff__row--added">
                <span className="dm-diff__what">{e.name}</span>
                {e.summary !== null && <span className="dm-diff__meta">{e.summary}</span>}
                <span className="dm-diff__meta">
                  {e.fields.map((f) => `${f.name}: ${f.type}`).join(' · ')}
                </span>
                <NoteList notes={e.notes} />
              </li>
            ))}
          </ul>
        </DiffGroup>
      )}
      {diff.entitiesRemoved.length > 0 && (
        <DiffGroup title="No longer tracked">
          <ul className="dm-diff__list">
            {diff.entitiesRemoved.map((e) => (
              <li key={e.name} className="dm-diff__row dm-diff__row--removed">
                <span className="dm-diff__what">{e.name}</span>
                {e.note !== undefined && <NoteList notes={[e.note]} />}
              </li>
            ))}
          </ul>
        </DiffGroup>
      )}
      {(diff.fieldsAdded.length > 0 || diff.fieldsRemoved.length > 0 || diff.fieldsRetyped.length > 0) && (
        <DiffGroup title="Fields">
          <ul className="dm-diff__list">
            {diff.fieldsAdded.map((f) => (
              <li key={`+${f.entity}.${f.field}`} className="dm-diff__row dm-diff__row--added">
                <span className="dm-diff__what">
                  {f.entity}.{f.field}
                </span>
                <span className="dm-diff__meta">added · {f.type}</span>
                <NoteList notes={f.notes} />
              </li>
            ))}
            {diff.fieldsRemoved.map((f) => (
              <li key={`-${f.entity}.${f.field}`} className="dm-diff__row dm-diff__row--removed">
                <span className="dm-diff__what">
                  {f.entity}.{f.field}
                </span>
                <span className="dm-diff__meta">removed · was {f.type}</span>
                {f.note !== undefined && <NoteList notes={[f.note]} />}
              </li>
            ))}
            {diff.fieldsRetyped.map((f) => (
              <li key={`~${f.entity}.${f.field}`} className="dm-diff__row dm-diff__row--changed">
                <span className="dm-diff__what">
                  {f.entity}.{f.field}
                </span>
                <span className="dm-diff__meta">
                  {f.from} → {f.to}
                </span>
              </li>
            ))}
          </ul>
        </DiffGroup>
      )}
      {(diff.relationshipsAdded.length > 0 ||
        diff.relationshipsRemoved.length > 0 ||
        diff.relationshipsChanged.length > 0) && (
        <DiffGroup title="Connections">
          <ul className="dm-diff__list">
            {diff.relationshipsAdded.map((r, i) => (
              <li key={`+${i}`} className="dm-diff__row dm-diff__row--added">
                <span className="dm-diff__what">
                  {r.from} → {r.to}
                </span>
                <span className="dm-diff__meta">added{r.rendered !== '' ? ` · ${r.rendered}` : ''}</span>
                {r.note !== undefined && <span className="dm-diff__note">{r.note}</span>}
              </li>
            ))}
            {diff.relationshipsRemoved.map((r, i) => (
              <li key={`-${i}`} className="dm-diff__row dm-diff__row--removed">
                <span className="dm-diff__what">
                  {r.from} → {r.to}
                </span>
                <span className="dm-diff__meta">removed{r.rendered !== '' ? ` · was ${r.rendered}` : ''}</span>
              </li>
            ))}
            {diff.relationshipsChanged.map((r, i) => (
              <li key={`~${i}`} className="dm-diff__row dm-diff__row--changed">
                <span className="dm-diff__what">
                  {r.from} → {r.to}
                </span>
                <span className="dm-diff__meta">
                  {r.before} → {r.after}
                </span>
              </li>
            ))}
          </ul>
        </DiffGroup>
      )}
      {(diff.lifecyclesAdded.length > 0 || diff.lifecyclesRemoved.length > 0 || diff.lifecyclesChanged.length > 0) && (
        <DiffGroup title="Lifecycles">
          <ul className="dm-diff__list">
            {diff.lifecyclesAdded.map((l) => (
              <li key={`+${l.name}`} className="dm-diff__row dm-diff__row--added">
                <span className="dm-diff__what">{l.entity ?? l.name}</span>
                <span className="dm-diff__meta">added · {l.rendered}</span>
              </li>
            ))}
            {diff.lifecyclesRemoved.map((l) => (
              <li key={`-${l.name}`} className="dm-diff__row dm-diff__row--removed">
                <span className="dm-diff__what">{l.entity ?? l.name}</span>
                <span className="dm-diff__meta">removed · was {l.rendered}</span>
              </li>
            ))}
            {diff.lifecyclesChanged.map((l) => (
              <li key={`~${l.name}`} className="dm-diff__row dm-diff__row--changed">
                <span className="dm-diff__what">{l.entity ?? l.name}</span>
                <span className="dm-diff__meta">
                  {l.before} → {l.after}
                </span>
              </li>
            ))}
          </ul>
        </DiffGroup>
      )}
      {(diff.rulesAdded.length > 0 || diff.rulesRemoved.length > 0 || diff.rulesChanged.length > 0) && (
        <DiffGroup title="Rules">
          <ul className="dm-diff__list">
            {diff.rulesAdded.map((r, i) => (
              <li key={`+${i}`} className="dm-diff__row dm-diff__row--added">
                {r.name !== '' && <span className="dm-diff__what">{r.name}</span>}
                <span className="dm-diff__meta">added · {r.expression}</span>
              </li>
            ))}
            {diff.rulesRemoved.map((r, i) => (
              <li key={`-${i}`} className="dm-diff__row dm-diff__row--removed">
                {r.name !== '' && <span className="dm-diff__what">{r.name}</span>}
                <span className="dm-diff__meta">removed · was {r.expression}</span>
              </li>
            ))}
            {diff.rulesChanged.map((r, i) => (
              <li key={`~${i}`} className="dm-diff__row dm-diff__row--changed">
                <span className="dm-diff__what">{r.name}</span>
                <span className="dm-diff__meta">
                  {r.before} → {r.after}
                </span>
              </li>
            ))}
          </ul>
        </DiffGroup>
      )}
      {diff.summaryChanged !== null && (
        <DiffGroup title="How the model describes itself">
          <ul className="dm-diff__list">
            <li className="dm-diff__row dm-diff__row--changed">
              <span className="dm-diff__meta">was: {diff.summaryChanged.before ?? '(nothing)'}</span>
              <span className="dm-diff__meta">now: {diff.summaryChanged.after ?? '(nothing)'}</span>
            </li>
          </ul>
        </DiffGroup>
      )}
      {diff.moneyChanged !== null && (
        <DiffGroup title="Money">
          <ul className="dm-diff__list">
            <li className="dm-diff__row dm-diff__row--changed">
              <span className="dm-diff__meta">was: {diff.moneyChanged.before}</span>
              <span className="dm-diff__meta">now: {diff.moneyChanged.after}</span>
            </li>
          </ul>
        </DiffGroup>
      )}
    </div>
  );
}

/** What the person's revision came to — every state its own plain sentence. */
export function DiffPanel({ diff }: { diff: RunDataModelDiffResult }) {
  if (diff.state === 'none') return null;
  if (diff.state === 'pending') {
    return (
      <section className="dm-diff dm-diff--pending" aria-label="Your requested changes">
        <h3 className="dm-diff__title">Your requested changes</h3>
        <CommentsQuote comments={diff.comments} />
        <p className="dm-diff__sentence">
          Your words are recorded — the machine has not derived the new model yet.
        </p>
      </section>
    );
  }
  if (diff.state === 'unchanged') {
    return (
      <section className="dm-diff dm-diff--unchanged" aria-label="Nothing changed">
        <h3 className="dm-diff__title">You asked — and the model is unchanged</h3>
        <CommentsQuote comments={diff.comments} />
        <p className="dm-diff__sentence">
          The machine derived the data model again with your words in front of it, and nothing in the
          model moved. Your request was not turned into a change. If it matters, say it differently — or
          approve the model as it stands.
        </p>
      </section>
    );
  }
  if (diff.state === 'closed') {
    return (
      <section className="dm-diff dm-diff--closed" aria-label="Model changed">
        <h3 className="dm-diff__title">The model changed after your request</h3>
        <CommentsQuote comments={diff.comments} />
        <p className="dm-diff__sentence">
          The run moved on before this comparison could be captured, so there is no honest diff to show
          here — the model below is the one the run continued with.
        </p>
      </section>
    );
  }
  return (
    <section className="dm-diff dm-diff--changed" aria-label="What changed">
      <h3 className="dm-diff__title">What changed since the version you read</h3>
      <CommentsQuote comments={diff.comments} />
      <p className="dm-diff__sentence">
        This is everything that moved — including anything you did not ask about.
      </p>
      <ChangedModel diff={diff.diff} />
    </section>
  );
}

// ── the grounded-intake brief (proposal item 2) ──────────────────────────────

/**
 * The compiled-brief review, riding the run's FIRST door: the claims the one-shot intake
 * extracted from the owner's brief — the same bytes the chain modeled against and the
 * spec-fidelity check will diff the shipped app against. A failed extraction renders its
 * honest record (the run continued ungrounded — v1 — and the verdict will say so).
 */
function IntakeBrief({ intake }: { intake: RunIntakeSurface }) {
  if (intake.status === 'failed' || intake.claims.length === 0) {
    return (
      <section className="dm-intake dm-intake--failed" role="region" aria-label="Brief intake">
        <h3 className="dm__section">Your brief could not be grounded</h3>
        <p className="dm-intake__note">
          {intake.status === 'failed'
            ? `The intake extraction failed${intake.reason !== undefined ? ` — ${intake.reason}` : ''}.`
            : 'The intake extraction found no claims in the brief.'}{' '}
          This run was built from the raw brief instead, and the final report will say the spec-fidelity check
          could not compute.
        </p>
      </section>
    );
  }
  return (
    <section className="dm-intake" role="region" aria-label="Brief intake">
      <h3 className="dm__section">
        Your brief, as understood — {intake.claims.length} claim{intake.claims.length === 1 ? '' : 's'}
      </h3>
      <p className="dm-intake__note">
        Each claim below was extracted verbatim from your brief. The data model on this page was derived from
        them, and the finished app will be checked against them — a claim the app does not honor becomes a
        question back to you, never a silent gap.
        {intake.degradedCalls !== undefined &&
          ` (${intake.degradedCalls} extraction call${intake.degradedCalls === 1 ? '' : 's'} degraded — part of the brief may not have been captured.)`}
      </p>
      <ul className="dm-intake__list">
        {intake.claims.map((c) => (
          <li key={c.id} className="dm-intake__row">
            <span className="dm-intake__kind">{c.kind}</span>
            <span className="dm-intake__title">{c.title}</span>
            {c.detail.trim() !== '' && c.detail.trim() !== c.title.trim() && (
              <span className="dm-intake__detail">{c.detail}</span>
            )}
          </li>
        ))}
      </ul>
      {intake.openDecisions.length > 0 && (
        <>
          <h4 className="dm-intake__subhead">Left open, with a default</h4>
          <ul className="dm-intake__list">
            {intake.openDecisions.map((od) => (
              <li key={od.id} className="dm-intake__row">
                <span className="dm-intake__title">{od.title}</span>
                <span className="dm-intake__detail">
                  {od.context} Default: {od.defaultDecision}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// ── the review itself ────────────────────────────────────────────────────────

export interface DataModelReviewProps {
  readonly payload: RunDataModelResult;
  /** What a submitted revision came to; null while it has not loaded. */
  readonly diff: RunDataModelDiffResult | null;
  /** Is the run actually waiting at this door right now (status says so AND disk says so)? */
  readonly door: boolean;
  readonly onApprove: () => Promise<ApproveDataModelResult>;
  readonly onRequestRevision: (comments: RevisionComment[]) => Promise<RequestDataModelRevisionResult>;
  /** The EDIT door's dry run — what one exact edit will do, server-computed, nothing committed. */
  readonly onPreviewEdit: (edit: DataModelEdit) => Promise<RunDataModelEditPreview>;
  /** The EDIT door — the deterministic, no-derivation half of the two doors. */
  readonly onEdit: (edit: DataModelEdit) => Promise<EditDataModelResult>;
  /** The BASKET's combined dry run — the whole list chained IN ORDER server-side. */
  readonly onPreviewBasket: (edits: DataModelEdit[]) => Promise<RunDataModelBasketPreview>;
  /** The BASKET's apply — all rows in one append, one resume, all-or-nothing on a refusal. */
  readonly onApplyBasket: (edits: DataModelEdit[]) => Promise<ApplyDataModelBasketResult>;
  /** The run's recorded owner orders (`runs.ownerEdits`) — the source of the ORDERED chips.
   *  Absent (or empty) ⇒ no chip, no ribbon: a screen with nothing ordered says nothing. */
  readonly orders?: readonly OwnerLedgerHistoryRow[];
}

/** The six ER-structural kinds an ORDERED chip can come from — the exact door's whole vocabulary. */
const ER_ORDER_KINDS: ReadonlySet<string> = new Set([
  'er-field-rename',
  'er-field-retype',
  'er-field-add',
  'er-field-remove',
  'er-entity-rename',
  'er-entity-remove',
]);

export function DataModelReview({
  payload,
  diff,
  door,
  onApprove,
  onRequestRevision,
  onPreviewEdit,
  onEdit,
  onPreviewBasket,
  onApplyBasket,
  orders,
}: DataModelReviewProps) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // Per-entity composer drafts (presence = the box is open) + the whole-model box.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [generalDraft, setGeneralDraft] = useState('');
  const [confirmingRevise, setConfirmingRevise] = useState(false);
  const [reviseNote, setReviseNote] = useState<string | null>(null);
  // The one open EDIT composer (one at a time — composing is a considered act; the BASKET below
  // is where several composed edits become one commitment).
  const [editEntity, setEditEntity] = useState<string | null>(null);

  // ── THE PICTURE (phase 2) ──────────────────────────────────────────────────────────
  // The default view, because a model is a shape before it is a list — and the Cards are one
  // click away, byte-identical to what they have always been. The state is local: a view
  // preference is not a fact about the run, so nothing is persisted and nothing is fetched.
  const [view, setView] = useState<'picture' | 'cards'>('picture');
  /** The entity whose card is open beneath the canvas. */
  const [selected, setSelected] = useState<string | null>(null);
  /** The gesture that prefilled the open composer (see {@link EditComposerSeed}). */
  const [seed, setSeed] = useState<(EditComposerSeed & { readonly entity: string }) | null>(null);

  /* THE DOOR CLOSING TAKES THE GESTURE STATE WITH IT. Without this, a composer left open —
     or a seed left primed — survives the close and resurrects at the NEXT halt (the model
     may have been re-derived underneath it), replaying an act nobody just performed. */
  useEffect(() => {
    if (!door) {
      setSeed(null);
      setEditEntity(null);
    }
  }, [door]);

  /** Open an entity beneath the picture. Selecting a DIFFERENT entity closes the composer the
   *  last gesture opened — a seeded form belongs to the box it was seeded from. */
  function selectEntity(name: string) {
    setSelected(name);
    if (editEntity !== name) {
      setEditEntity(null);
      setSeed(null);
    }
  }

  /** A gesture on the canvas: select the box, open ITS composer, and prefill it. Nothing is
   *  sent anywhere — this is the same door the cards open, with the form already filled in. */
  function gesture(op: EditOp, entity: string, field?: string) {
    setSelected(entity);
    setEditEntity(entity);
    setSeed((prior) => ({
      entity,
      op,
      ...(field !== undefined ? { field } : {}),
      // Only a COMPLETE gesture may run the dry run at once (the two removals name everything).
      autoPreview: op === 'remove-field' || op === 'remove-entity',
      token: (prior?.token ?? 0) + 1,
    }));
  }

  /* The gestures exist only while the door is open — exactly like the cards' own affordances.
     A closed door draws the same picture with nothing to press. */
  const gestures: CanvasGestures | null = door
    ? {
        onRenameField: (entity, field) => gesture('rename-field', entity, field),
        onRetypeField: (entity, field) => gesture('retype-field', entity, field),
        onAddField: (entity) => gesture('add-field', entity),
        onRemoveField: (entity, field) => gesture('remove-field', entity, field),
        onRenameEntity: (entity) => gesture('rename-entity', entity),
        onRemoveEntity: (entity) => gesture('remove-entity', entity),
      }
    : null;

  /* ORDERED — a different PROVENANCE, never a pending state. The chain applies ER-structural
     orders AT the ER boundary, BEFORE this halt (newpip chain.ts, F8: approving the pre-patch
     model and building the patched one is the F6 defect), so the model on this screen already
     reflects every active order the patcher could apply — nothing here is "waiting". What the
     chips and the ribbon mark is WHOSE a change is: ordered by the owner, not derived by the
     machine. They render only while the door is open, styled brass, and counted from the
     ledger's own structured `about` anchor — never by reading prose. */
  const orderedCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!door) return counts;
    for (const row of orders ?? []) {
      if (!row.active || row.about === undefined || !ER_ORDER_KINDS.has(row.kind)) continue;
      const entity = row.about.split('.')[0];
      counts.set(entity, (counts.get(entity) ?? 0) + 1);
    }
    return counts;
  }, [orders, door]);
  const orderedTotal = [...orderedCounts.values()].reduce((sum, n) => sum + n, 0);

  // ── THE BASKET: several exact edits, one combined preview, one apply, one resume ────
  // Order is significant (each edit lands on the model as the ones before it left it), so the list
  // is ordered and reorderable. EVERY mutation bumps `basketVersion` and drops the combined preview
  // — a preview must only ever describe the basket on screen (the composer's chained preview
  // subscribes to the same version for the same reason).
  const [basket, setBasket] = useState<DataModelEdit[]>([]);
  const [basketVersion, setBasketVersion] = useState(0);
  const [basketPreview, setBasketPreview] = useState<RunDataModelBasketPreview | null>(null);
  const [basketBusy, setBasketBusy] = useState(false);
  const [basketNote, setBasketNote] = useState<string | null>(null);

  function mutateBasket(next: (prior: DataModelEdit[]) => DataModelEdit[]) {
    setBasket(next);
    setBasketPreview(null);
    setBasketNote(null);
    setBasketVersion((v) => v + 1);
  }

  const addToBasket = (edit: DataModelEdit) => mutateBasket((b) => [...b, edit]);
  const removeFromBasket = (index: number) => mutateBasket((b) => b.filter((_, i) => i !== index));
  const moveInBasket = (index: number, delta: -1 | 1) =>
    mutateBasket((b) => {
      const to = index + delta;
      if (to < 0 || to >= b.length) return b;
      const next = [...b];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });

  /** The composer's preview while the basket holds edits: CHAINED — the candidate is dry-run as the
   *  basket's would-be next member, so what it shows is what the basket apply would actually do. The
   *  single-edit preview against pre-basket bytes would show a consequence that is not true. */
  async function previewChainedAfterBasket(edit: DataModelEdit): Promise<RunDataModelEditPreview> {
    const combined = await onPreviewBasket([...basket, edit]);
    const last = combined.items[combined.items.length - 1];
    if (last.outcome.kind === 'refused') return { kind: 'refused', refusals: last.outcome.refusals };
    if (last.outcome.kind === 'no-op') return { kind: 'no-change', reason: last.outcome.reason };
    return {
      kind: 'change',
      changes: last.outcome.changes,
      cautions: last.outcome.cautions,
      alreadyOrdered: false, // basket policy folds an already-ordered edit into a refusal above
      laterStagesDerived: combined.laterStagesDerived,
    };
  }

  async function runBasketPreview() {
    setBasketBusy(true);
    setBasketNote(null);
    try {
      setBasketPreview(await onPreviewBasket(basket));
    } catch (err) {
      setBasketNote(runErrorMessage(err));
    } finally {
      setBasketBusy(false);
    }
  }

  async function applyBasket() {
    setBasketBusy(true);
    setBasketNote(null);
    try {
      const result = await onApplyBasket(basket);
      setBasketNote(basketResultSentence(result));
      if (result.outcome === 'refused') {
        // All-or-nothing held: nothing landed. The disk moved under the shown preview — show the
        // server's own re-checked outcomes so the refusal is attributed on screen, basket intact.
        setBasketPreview({
          items: result.items,
          applicable: false,
          laterStagesDerived: basketPreview?.laterStagesDerived ?? false,
        });
      } else {
        // Applied (or everything already true): the basket is spent.
        setBasket([]);
        setBasketPreview(null);
        setBasketVersion((v) => v + 1);
      }
    } catch (err) {
      setBasketNote(runErrorMessage(err));
    } finally {
      setBasketBusy(false);
    }
  }

  const model = payload.model;
  if (model === null) return null; // absence renders upstream (the artifact pane) — never here

  const approval = payload.approval;
  const openDecisions = approval.state === 'awaiting' ? approval.openDecisions : 0;

  const staged: RevisionComment[] = [
    ...Object.entries(drafts)
      .filter(([, text]) => text.trim() !== '')
      .map(([entity, text]) => ({ text, about: entity })),
    ...(generalDraft.trim() !== '' ? [{ text: generalDraft }] : []),
  ];

  async function approve() {
    setBusy(true);
    setNote(null);
    try {
      setNote(approveResultSentence(await onApprove()));
    } catch (err) {
      setNote(runErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitRevision() {
    setBusy(true);
    setReviseNote(null);
    try {
      const result = await onRequestRevision(staged);
      setReviseNote(revisionResultSentence(result));
      if (result.recorded > 0) {
        setDrafts({});
        setGeneralDraft('');
      }
    } catch (err) {
      setReviseNote(runErrorMessage(err));
    } finally {
      setBusy(false);
      setConfirmingRevise(false);
    }
  }

  /* ONE CARD COMPONENT, TWO VIEWS. The Picture reaches the card; it never replaces it — the
     selected entity renders the SAME `EntityCard`, with the same disagree box, the same
     composer and the same door gating it has in the Cards list. There is no second code path
     for editing, and there is nothing the Picture can offer that the Cards cannot. */
  const entityCard = (e: DataModelEntity) => (
    <EntityCard
      key={e.name}
      entity={e}
      revisable={door}
      draft={drafts[e.name]}
      onOpen={() => setDrafts((d) => ({ ...d, [e.name]: '' }))}
      onDraft={(text) => setDrafts((d) => ({ ...d, [e.name]: text }))}
      onOpenEdit={() => {
        setEditEntity(e.name);
        setSeed(null); // opened by hand: no gesture prefilled it
      }}
      editor={
        door && editEntity === e.name ? (
          <EditComposer
            entity={e}
            seed={seed !== null && seed.entity === e.name ? seed : null}
            onSeedConsumed={() => setSeed(null)}
            onPreview={basket.length === 0 ? onPreviewEdit : previewChainedAfterBasket}
            onApply={onEdit}
            onClose={() => {
              setEditEntity(null);
              setSeed(null);
            }}
            onAdd={addToBasket}
            basketCount={basket.length}
            basketVersion={basketVersion}
          />
        ) : null
      }
    />
  );

  const selectedEntity = model.entities.find((e) => e.name === selected) ?? null;

  return (
    <section className="dm" aria-label="Data model">
      <div className="dm__head">
        <h2 className="dm__title">Data model</h2>
        <p className="dm__lede">
          {model.entities.length} {model.entities.length === 1 ? 'entity' : 'entities'} ·{' '}
          {model.relationships.length} relationship{model.relationships.length === 1 ? '' : 's'}
        </p>
      </div>

      {model.summary !== null && <p className="dm__summary">{model.summary}</p>}

      {/* GROUNDED INTAKE (proposal item 2): the compiled-brief review rides the run's first
          door — what the machine understood, beside the model it derived from it. Absent on
          pre-intake (v1) runs, whose screen is byte-identical to before. */}
      {payload.intake !== undefined && <IntakeBrief intake={payload.intake} />}

      {door && (
        <div className="dm-door" role="region" aria-label="Approval">
          <p className="dm-door__sentence">{doorSentence(approval)}</p>
          {openDecisions > 0 && (
            <p className="dm-door__hold">
              {openDecisions} decision{openDecisions === 1 ? '' : 's'} about this model still need
              {openDecisions === 1 ? 's' : ''} your answer — approving alone will not release the run.
            </p>
          )}
          <div className="dm-door__actions">
            <button className="btn btn--primary" type="button" onClick={() => void approve()} disabled={busy}>
              {busy ? 'Approving…' : 'Approve and continue'}
            </button>
          </div>
          <p className="dm-door__aside">
            Something wrong? Two doors, and they are not the same. “Change something” on an entity below is
            EXACT: you pick the change, it is applied as ordered — no re-derivation, nothing spent to make
            it — and several changes can go into one basket, previewed together and applied as one.
            “Disagree” sends your own words and the machine derives the model again around them — that
            is paid work, and a request, not a guarantee.
          </p>
        </div>
      )}

      {/* The approve outcome OUTLIVES the door: approving flips the payload to 'approved'
          and unmounts the door block, and the confirmation must not vanish with it. */}
      {note !== null && (
        <p className="dm-door__note" role="status">
          {note}
        </p>
      )}

      {/* What a submitted revision came to — pending / unchanged / changed / closed.
          Above the model, so the person sees what moved BEFORE re-approving. */}
      {diff !== null && <DiffPanel diff={diff} />}

      {/* THE ORDERS TRAY: the basket, docked. The composed set, its order, the combined
          preview, the one apply — every law verbatim; only where it lives changed. The
          result note outlives the emptied basket (the approve-note law above). */}
      {door && (
        <OrdersTray
          basket={basket}
          preview={basketPreview}
          busy={basketBusy}
          note={basketNote}
          onMove={moveInBasket}
          onRemove={removeFromBasket}
          onEmpty={() => mutateBasket(() => [])}
          onPreview={() => void runBasketPreview()}
          onApply={() => void applyBasket()}
          onDismissPreview={() => setBasketPreview(null)}
        />
      )}

      {!door && approval.state === 'approved' && (
        <p className="dm-approved" role="note">
          You approved this data model
          {approval.approvedAt !== null ? ` on ${new Date(approval.approvedAt).toLocaleString()}` : ''}. The
          approval is tied to this exact model — if it is rebuilt later, the run will pause here again for a
          fresh look.
        </p>
      )}

      {/* ORDERED — provenance, not pendingness: the orders are folded in before this halt is
          offered (see the note above), so the ribbon says what stands, never what "waits". */}
      {orderedTotal > 0 && (
        <p className="dm-ordered-ribbon" role="note">
          {orderedTotal} change{orderedTotal === 1 ? '' : 's'} ordered on this model —{' '}
          {orderedTotal === 1 ? 'it is yours' : 'they are yours'}, exactly as ordered, and{' '}
          {orderedTotal === 1 ? 'it stands' : 'they stand'} on every future run of this build.
        </p>
      )}

      <div className="dm__section-head">
        <h3 className="dm__section">What the app will keep track of</h3>
        <div className="dm-view" role="group" aria-label="How to show the model">
          <button
            className={`btn btn--sm ${view === 'picture' ? 'btn--ghost dm-view__on' : 'btn--quiet'}`}
            type="button"
            aria-pressed={view === 'picture'}
            onClick={() => setView('picture')}
          >
            Picture
          </button>
          <button
            className={`btn btn--sm ${view === 'cards' ? 'btn--ghost dm-view__on' : 'btn--quiet'}`}
            type="button"
            aria-pressed={view === 'cards'}
            onClick={() => setView('cards')}
          >
            Cards
          </button>
        </div>
      </div>

      {view === 'picture' ? (
        <>
          <DataModelCanvas
            model={model}
            selected={selected}
            onSelect={selectEntity}
            gestures={gestures}
            orderedCounts={orderedCounts}
          />
          <p className="dm__aside">
            {door
              ? 'Click a box to open that entity below. Clicking a field’s name, or its type, opens the change composer already filled in — nothing is committed until you say so.'
              : 'Click a box to read that entity below. Pan inside the frame.'}{' '}
            ● is the field that identifies it · ⚙ means its values move through a lifecycle · a dashed line
            is an optional connection.
          </p>
          {selectedEntity !== null ? (
            <div className="dm-entities dm-entities--one">{entityCard(selectedEntity)}</div>
          ) : (
            <p className="dm__aside">
              Nothing is open yet. The Cards view lists every entity in full, with the machine’s own reasons.
            </p>
          )}
        </>
      ) : (
        <div className="dm-entities">{model.entities.map(entityCard)}</div>
      )}

      {model.relationships.length > 0 && (
        <>
          <h3 className="dm__section">How they connect</h3>
          <p className="dm__aside">
            The same connections the Picture draws, written out — a connection IS a field one thing
            keeps to name another, and this list says which field.
          </p>
          <ul className="dm-rels">
            {model.relationships.map((r, i) => (
              <li key={i} className="dm-rel">
                <span className="dm-rel__line">
                  {r.from} → {r.to}
                  {r.cardinality !== null && <span className="dm-rel__meta"> · {r.cardinality}</span>}
                  {r.foreignKeyField !== null && <span className="dm-rel__meta"> · via {r.foreignKeyField}</span>}
                  {r.optional === true && <span className="dm-rel__meta"> · optional</span>}
                </span>
                {r.note !== undefined && <span className="dm-rel__note">{r.note}</span>}
              </li>
            ))}
          </ul>
        </>
      )}

      {model.lifecycles.length > 0 && (
        <>
          <h3 className="dm__section">Lifecycles</h3>
          <ul className="dm-lifecycles">
            {model.lifecycles.map((l) => (
              <li key={l.name} className="dm-lifecycle">
                <span className="dm-lifecycle__head">
                  {l.entity ?? l.name}: {l.values.join(' · ')}
                  {l.initial !== null && <span className="dm-lifecycle__meta"> (starts at {l.initial})</span>}
                </span>
                {l.transitions.length > 0 && (
                  <span className="dm-lifecycle__moves">
                    {l.transitions.map((t) => `${t.from} → ${t.to}${t.event !== '' ? ` on ${t.event}` : ''}`).join(' · ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {model.rules.length > 0 && (
        <>
          <h3 className="dm__section">Rules it will enforce</h3>
          <ul className="dm-rules">
            {model.rules.map((r, i) => (
              <li key={i} className="dm-rule">
                {r.name !== '' && <span className="dm-rule__name">{r.name}</span>}
                <span className="dm-rule__expr">{r.expression}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {model.money !== null && (
        <>
          <h3 className="dm__section">Money</h3>
          <dl className="dm-money">
            {model.money.representation !== null && (
              <div>
                <dt>Stored as</dt>
                <dd>{model.money.representation}</dd>
              </div>
            )}
            {model.money.currencyCode !== null && (
              <div>
                <dt>Currency</dt>
                <dd>{model.money.currencyCode}</dd>
              </div>
            )}
            {model.money.totalsTaxTreatment !== null && (
              <div>
                <dt>Totals</dt>
                <dd>{model.money.totalsTaxTreatment}</dd>
              </div>
            )}
          </dl>
          {model.money.invariants.length > 0 && (
            <ul className="dm-invariants">
              {model.money.invariants.map((inv, i) => (
                <li key={i}>
                  <code>{inv}</code>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {model.modelNotes.length > 0 && (
        <>
          <h3 className="dm__section">Choices made for the whole model</h3>
          <NoteList notes={model.modelNotes} />
        </>
      )}

      {door && (
        <div className="dm-revise" role="region" aria-label="Ask for changes">
          <h3 className="dm__section">Something wrong?</h3>
          <label className="dm-revise__label" htmlFor="dm-revise-general">
            Say it in your own words — about the whole model, or anything the boxes above don’t cover.
          </label>
          <textarea
            id="dm-revise-general"
            className="dm-revise__text"
            rows={3}
            value={generalDraft}
            onChange={(e) => setGeneralDraft(e.target.value)}
          />
          {staged.length > 0 && !confirmingRevise && (
            <div className="dm-revise__actions">
              <button className="btn btn--ghost" type="button" onClick={() => setConfirmingRevise(true)} disabled={busy}>
                Ask for changes ({staged.length})
              </button>
            </div>
          )}
          {staged.length > 0 && confirmingRevise && (
            <div className="dm-revise__confirm" role="region" aria-label="Before it runs">
              <p className="dm-revise__consequence">
                Before it runs: the machine will derive the data model again with your words in front of
                it. Everything after the data model is built again too — it all builds on what changes
                here. Your recorded answers stay recorded, and the run pauses here again so you can see
                exactly what changed before approving.
              </p>
              <div className="dm-revise__actions">
                <button className="btn btn--primary" type="button" onClick={() => void submitRevision()} disabled={busy}>
                  {busy ? 'Sending…' : 'Re-derive the data model'}
                </button>
                <button
                  className="btn btn--quiet"
                  type="button"
                  onClick={() => setConfirmingRevise(false)}
                  disabled={busy}
                >
                  Keep reading
                </button>
              </div>
            </div>
          )}
          {reviseNote !== null && (
            <p className="dm-door__note" role="status">
              {reviseNote}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
